/**
 * @fileoverview The register's transaction ledger (`transactions`) — what a sale's header names through its reference
 * pair, its type, and the statuses a row can hold. Declared once.
 *
 * ## Why it lives here
 *
 * The `transactions` table is pos-api's: the register writes it. Its vocabularies were declared only in pos-api's
 * `constants/statuses.js`, so nothing outside pos-api could read a register sale by name. REGISTER-WEB-ORDERS-001 B7b
 * (GAMING 1341Z) reads the money a register sale holds on a PurchasePuffin order from purchase-api as well
 * (`@puffin-core/order-engine` `registerMoney`), so the vocabularies MOVED here, unchanged, with every pos-api reader;
 * pos-api no longer declares them.
 *
 * @module @puffin-core/status-taxonomy/registerTransaction
 */

'use strict';

/**
 * What a sale's header names through its polymorphic `referenceType` / `referenceId` pair: the order it
 * settles, the invoice it pays (OWNER-RULINGS-WALK-BUILDS L1, owner ruling Q-L1-3), or the mailbox rental
 * whose rent it is (MAILBOX-RENT-PAYMENTS-001 U2, owner ruling R-SSC-3: online rent renews the rental, and the
 * register posts the same record). `payments` carries no invoice column, so an invoice reaches its tenders
 * through this pair (pos-api `Payment#findByInvoice`); a rental reaches its rent sales the same way.
 *
 * @type {Readonly<Object<string, string>>}
 */
const TRANSACTION_REFERENCE_TYPE = Object.freeze({
  ORDER: 'order',
  INVOICE: 'invoice',
  MAILBOX_RENTAL: 'mailbox_rental',
});

/**
 * What a transaction is.
 *
 * @type {Readonly<Object<string, string>>}
 */
const TRANSACTION_TYPE = Object.freeze({
  SALE: 'sale',
  REFUND: 'refund',
  VOID: 'void',
  ADJUSTMENT: 'adjustment',
});

/**
 * The transaction statuses pos-api WRITES — the vocabulary every create / status-update validator
 * and pos-api's `Transaction.VALID_STATUSES` accept.
 *
 * @type {Readonly<Object<string, string>>}
 */
const TRANSACTION_STATUS = Object.freeze({
  PENDING: 'pending',
  COMPLETED: 'completed',
  FAILED: 'failed',
  REVERSED: 'reversed',
});

/**
 * The VOIDED status — one `transactions.status` value that no create and no status update writes.
 *
 * Two writers reach it, and both stand outside the status machine:
 *
 * - **the void** (STORE-ALERTS-001 U4, owner rulings Q-SA-VOID-MONEY and Q-SA-VOID-REPORTS, 2026-10-10):
 *   `POST /financial/transactions/:id/void` moves a completed sale `completed → voided` on its own store day,
 *   gives every tender back through the ONE refund engine, and STAMPS it (`voidedAt` / `voidedBy` / `voidReason`,
 *   set together — pos `20261006150920`). A stamped void counts as neither a sale nor a refund;
 * - **the PostalMate importer**: `TransactionTransformer.transformStatus` maps `VOID === 1` to `voided` — 6,852 D Town
 *   rows (UNREACHABLE-MODULE-BURNDOWN-001 L13b / Q-L13-4, 2026-09-25), carrying no stamp and no reversal.
 *
 * Kept APART from {@link TRANSACTION_STATUS} on purpose: a register cannot create a voided sale, and the ONE status seam
 * (`Transaction.transitionStatus`) moves a row into `voided` only stamped, for the void after it has given the money
 * back (`Transaction.stampVoid`), so write validators must not accept it; a READ filter must, or an operator cannot ask for the rows the database holds.
 *
 * @type {Readonly<Object<string, string>>}
 */
const VOID_TRANSACTION_STATUS = Object.freeze({
  VOIDED: 'voided',
});

/**
 * Every value `transactions.status` can hold. Read filters validate against THIS.
 *
 * @type {ReadonlyArray<string>}
 */
const PERSISTED_TRANSACTION_STATUSES = Object.freeze([
  ...Object.values(TRANSACTION_STATUS),
  ...Object.values(VOID_TRANSACTION_STATUS),
]);

module.exports = {
  TRANSACTION_REFERENCE_TYPE,
  TRANSACTION_TYPE,
  TRANSACTION_STATUS,
  VOID_TRANSACTION_STATUS,
  PERSISTED_TRANSACTION_STATUSES,
};
